// Decoding seam for the phone scanner — SCAN-5 final: zxing-cpp compiled to
// WebAssembly (zxing-wasm) is THE engine, everywhere. The native
// BarcodeDetector tier and the @zxing/library js fallback were removed after
// the on-device A/B (decisions.md 2026-08-28): one pinned binary behaves
// identically on every device, and the dual-path bridge code was the real bug
// surface. Browser-only.
//
// This file stays the STABLE SEAM: decodeFromVideo / decodeAllFromImage keep
// their names and contracts (the portal suites mock decodeFromVideo via
// importOriginal), while the heavy engine (engine-wasm.ts: glue + ~1 MiB wasm)
// loads ONLY through the dynamic import below — never in a portal's eager
// graph, never under vitest.

import type * as WasmEngine from './engine-wasm.js'; // type-only — erased, never in the eager graph

// 'isbn' is the book-intake refinement: EAN-13 decoder ONLY plus a Bookland
// (978/979) sieve in the engine — a store inventory sticker, price-point UPC,
// or accession label can neither win first-hit nor cost decode time.
export type ScanMode = 'qr' | 'barcode' | 'both' | 'isbn';
export interface Box { x: number; y: number; w: number; h: number; } // intrinsic video px
export interface Decoded { text: string; format?: string; box?: Box; }

/* ------------------------------ engine loading ----------------------------- */

let wasmEngine: Promise<typeof WasmEngine> | null = null;
// Clear the cache on rejection (unlike a bare `||=`): a transient chunk-fetch
// failure (LAN blip, redeploy pulling the hashed chunk) must not brick the
// scanner until reload — the next tick retries.
const loadWasmEngine = () =>
  (wasmEngine ||= import('./engine-wasm.js').then(
    (m) => m,
    (e) => { wasmEngine = null; throw e; },
  ));

/** Opportunistic warm-up (camera start / scanner mount): fetch + instantiate
 * the wasm now so the first live frame pays decode cost only. Failures are
 * swallowed — the decode path retries and reports its own misses. */
export function warmUpEngine(): Promise<void> {
  return loadWasmEngine().then((m) => m.warmUp()).catch(() => undefined);
}

/* ---------------------------------- API ----------------------------------- */

/** Decode EVERY matching code found in a still image (bulk). Never rejects — [] on failure. */
export async function decodeAllFromImage(file: File, mode: ScanMode = 'both'): Promise<Decoded[]> {
  try { return await (await loadWasmEngine()).wasmDecodeImage(file, mode); } catch { return []; }
}

/** Decode a single code from a live <video> frame, with a bounding box for the
 * overlay. Never rejects — null on miss OR any failure (a rejected tick would
 * kill the scan loop). */
export async function decodeFromVideo(video: HTMLVideoElement, mode: ScanMode = 'both'): Promise<Decoded | null> {
  try { return await (await loadWasmEngine()).wasmDecodeVideo(video, mode); } catch { return null; }
}
